// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Close,
  ArrowBack,
  ArrowForward,
  Save
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../config/api';
import TemplateStep from './wizard/TemplateStep';
import BasicInfoStep from './wizard/BasicInfoStep';
import CategoriesStep from './wizard/CategoriesStep';
import QualificationItemsStep from './wizard/QualificationItemsStep';
import QAContextSettingsStep from './wizard/QAContextSettingsStep';
import QAContextPreviewStep from './wizard/QAContextPreviewStep';
import ReviewStep from './wizard/ReviewStep';

/**
 * QACriteriaWizard Component
 * 
 * 6-step wizard for creating new QA criteria:
 * Step 1: Template Selection
 * Step 2: Basic Information
 * Step 3: Categories & Metrics
 * Step 4: QA Context Settings
 * Step 5: QA Context Preview
 * Step 6: Review & Create
 */
const QACriteriaWizard = ({ open, onClose, onSuccess, criteriaList }) => {
  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Template state
  const [templateMode, setTemplateMode] = useState('scratch');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    team_id: '',
    criteria_type: 'matrix',
    description: '',
    is_active: true,
    is_default: false,
    categories: [],
    qa_context: {
      conversation_type: '',
      participants: '',
      conversation_objective: '',
      ideal_approach: '',
      context_notes: '',
      target_audience: ''
    },
    qa_context_preview: null
  });

  const [errors, setErrors] = useState({});

  const steps = [
    'Template',
    'Basic Info',
    formData.criteria_type === 'boolean' ? 'Qualification Items' : 'Categories',
    'QA Context',
    'Context Preview',
    'Review'
  ];

  // Load template when selected
  useEffect(() => {
    if (templateMode === 'copy' && selectedTemplate) {
      const template = criteriaList.find(c => c.id === selectedTemplate);
      if (template) {
        // Fetch full details with categories and metrics
        fetchTemplateDetails(selectedTemplate);
      }
    }
  }, [selectedTemplate, templateMode]);

  /**
   * Fetch full template details
   */
  const fetchTemplateDetails = async (criteriaId) => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.QA_CRITERIA_DETAIL}/${criteriaId}`);
      if (response.data.success && response.data.criteria) {
        const template = response.data.criteria;
        console.log('🔍 Loading template data:', template);
        console.log('🔍 Template QA context fields:', {
          conversation_type: template.conversation_type,
          participants: template.participants,
          conversation_objective: template.conversation_objective,
          ideal_approach: template.ideal_approach,
          context_notes: template.context_notes,
          target_audience: template.target_audience,
          high_level_context: template.high_level_context
        });
        
        const mappedCategories = (template.categories || []).map((c) => ({
          ...c,
          use_knowledge_for_analysis: false
        }));

        setFormData({
          name: `${template.name} (Copy)`,
          team_id: template.team_id || '',
          criteria_type: template.criteria_type || 'matrix',
          description: template.description || '',
          is_active: true,
          is_default: false,
          categories: mappedCategories,
          qa_context: {
            conversation_type: template.conversation_type || '',
            participants: template.participants || '',
            conversation_objective: template.conversation_objective || '',
            ideal_approach: template.ideal_approach || '',
            context_notes: template.context_notes || '',
            target_audience: template.target_audience || ''
          },
          qa_context_preview: template.high_level_context ? {
            preview: template.high_level_context,
            processing_time: null,
            model_used: 'template'
          } : null
        });
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      setError('Failed to load template details');
    }
  };

  /**
   * Handle template mode change (Start from Scratch vs Copy from Existing)
   */
  const handleTemplateModeChange = (mode) => {
    setTemplateMode(mode);
    if (mode === 'scratch') {
      setSelectedTemplate(null);
      setKbScope('all');
      setKbIds([]);
      setFormData((prev) => {
        const criteriaType = prev.criteria_type || 'matrix';
        const emptyCategories = criteriaType === 'boolean'
          ? [{
            category_name: 'Qualification',
            category_key: 'qualification',
            weight_percentage: 0,
            metrics: [],
            use_knowledge_for_analysis: false
          }]
          : [];
        return {
          ...prev,
          name: '',
          team_id: '',
          description: '',
          is_active: true,
          is_default: false,
          categories: emptyCategories
        };
      });
    }
  };

  /**
   * Handle field changes
   */
  const handleChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'criteria_type') {
        if (value === 'boolean' && (!prev.categories || prev.categories.length === 0)) {
          next.categories = [{
            category_name: 'Qualification',
            category_key: 'qualification',
            weight_percentage: 0,
            metrics: [],
            use_knowledge_for_analysis: false
          }];
        } else if (value === 'matrix') {
          next.categories = [];
        }
      }
      return next;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  /**
   * Update qualification items (single category metrics) for boolean criteria
   */
  const handleQualificationItemsChange = (newCategories) => {
    setFormData(prev => ({ ...prev, categories: newCategories }));
  };

  /**
   * Handle category changes
   */
  const handleCategoryChange = (index, updatedCategory) => {
    const newCategories = [...formData.categories];
    newCategories[index] = updatedCategory;
    setFormData(prev => ({ ...prev, categories: newCategories }));
  };

  /**
   * Add new category
   */
  const handleAddCategory = () => {
    const newCategory = {
      category_name: '',
      category_key: `category-${Date.now()}`,
      description: '',
      weight_percentage: 0,
      display_order: formData.categories.length,
      color_code: null,
      metrics: [],
      use_knowledge_for_analysis: false
    };
    setFormData(prev => ({
      ...prev,
      categories: [...prev.categories, newCategory]
    }));
  };

  /**
   * Delete category
   */
  const handleDeleteCategory = (index) => {
    const newCategories = formData.categories.filter((_, idx) => idx !== index);
    setFormData(prev => ({ ...prev, categories: newCategories }));
  };

  /**
   * Validate current step
   */
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Template
        if (templateMode === 'copy' && !selectedTemplate) {
          newErrors.template = 'Please select a template to copy from';
        }
        break;

      case 1: // Basic Info
        if (!formData.name || formData.name.trim().length < 3) {
          newErrors.name = 'Name must be at least 3 characters';
        }
        if (!formData.team_id || formData.team_id.trim().length === 0) {
          newErrors.team_id = 'Team is required';
        }
        break;

      case 2: // Categories or Qualification Items
        if (formData.criteria_type === 'boolean') {
          const qualMetrics = formData.categories?.[0]?.metrics || [];
          if (qualMetrics.length === 0) {
            newErrors.categories = 'At least one qualification item is required';
          } else {
            const itemErrors = {};
            qualMetrics.forEach((m, i) => {
              if (!m.description || !String(m.description).trim()) {
                itemErrors[i] = 'Qualification Question is required';
              }
            });
            if (Object.keys(itemErrors).length > 0) {
              newErrors.qualificationItemErrors = itemErrors;
            }
          }
        } else {
          if (formData.categories.length === 0) {
            newErrors.categories = 'At least one category is required';
          }

          const totalCategoryWeight = formData.categories.reduce(
            (sum, cat) => sum + (cat.weight_percentage || 0),
            0
          );

          if (totalCategoryWeight !== 100) {
            newErrors.categories = `Total category weights must equal 100% (currently ${totalCategoryWeight}%)`;
          }

          const categoryErrors = {};
          formData.categories.forEach((category, catIndex) => {
            const catErrors = {};

            if (!category.category_name || category.category_name.trim().length < 3) {
              catErrors.category_name = 'Category name must be at least 3 characters';
            }

            if (!category.metrics || category.metrics.length === 0) {
              catErrors.metrics = 'At least one metric is required per category';
            }

            const totalMetricWeight = (category.metrics || []).reduce(
              (sum, metric) => sum + (metric.weight_percentage || 0),
              0
            );

            if (totalMetricWeight !== category.weight_percentage) {
              catErrors.metricsWeightError = `Metric weights must equal category weight ${category.weight_percentage}% (currently ${totalMetricWeight}%)`;
            }

            if (Object.keys(catErrors).length > 0) {
              categoryErrors[catIndex] = catErrors;
            }
          });

          if (Object.keys(categoryErrors).length > 0) {
            newErrors.categoryErrors = categoryErrors;
          }
        }
        break;

      case 3: // QA Context Settings
        const context = formData.qa_context;
        if (!context?.conversation_type) {
          newErrors.conversation_type = 'Conversation type is required';
        }
        if (!context?.participants) {
          newErrors.participants = 'Participants is required';
        }
        if (!context?.target_audience) {
          newErrors.target_audience = 'Target audience is required';
        }
        if (!context?.conversation_objective || context.conversation_objective.trim().length < 10) {
          newErrors.conversation_objective = 'Conversation objective must be at least 10 characters';
        }
        if (!context?.ideal_approach || context.ideal_approach.trim().length < 10) {
          newErrors.ideal_approach = 'Ideal approach must be at least 10 characters';
        }
        break;

      case 4: // QA Context Preview
        if (!formData.qa_context_preview?.preview) {
          newErrors.qaContextPreview = 'QA context preview must be generated before proceeding';
        }
        break;

      case 5: // Review (final validation)
        // Run all validations
        return validateStep(1) && validateStep(2) && validateStep(3) && validateStep(4);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle next step
   */
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  /**
   * Handle back step
   */
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  /**
   * Handle create
   */
  const handleCreate = async () => {
    // Final validation
    if (!validateStep(1) || !validateStep(2) || !validateStep(3) || !validateStep(4)) {
      setError('Please fix all validation errors');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Prepare data for API
      const createData = {
        name: formData.name,
        team_id: formData.team_id,
        criteria_type: formData.criteria_type || 'matrix',
        description: formData.description || null,
        is_active: formData.is_active,
        is_default: formData.is_default,
        categories: formData.categories.map((category, catIndex) => ({
          category_name: category.category_name,
          category_key: category.category_key || `category-${catIndex}`,
          description: category.description || null,
          weight_percentage: category.weight_percentage,
          display_order: catIndex,
          color_code: category.color_code || null,
          use_knowledge_for_analysis: false,
          metrics: category.metrics.map((metric, metricIndex) => {
            const isBoolean = formData.criteria_type === 'boolean';
            const metricName = metric.metric_name?.trim() || (isBoolean && metric.description ? String(metric.description).slice(0, 50) : null) || `Item ${metricIndex + 1}`;
            return {
              metric_name: metricName,
              metric_key: metric.metric_key || `metric-${catIndex}-${metricIndex}`,
              description: metric.description || null,
              weight_percentage: metric.weight_percentage ?? 0,
              possible_scores: isBoolean ? (metric.possible_scores || ['Yes', 'No', 'N/A']) : (metric.possible_scores || null),
              evaluation_guide: metric.evaluation_guide || null,
              keywords: metric.keywords || null,
              is_mandatory: isBoolean ? (metric.is_mandatory ?? true) : undefined,
              display_order: metricIndex
            };
          })
        })),
        // QA Context Settings
        conversation_type: formData.qa_context?.conversation_type || null,
        participants: formData.qa_context?.participants || null,
        conversation_objective: formData.qa_context?.conversation_objective || null,
        ideal_approach: formData.qa_context?.ideal_approach || null,
        context_notes: formData.qa_context?.context_notes || null,
        target_audience: formData.qa_context?.target_audience || null,
        high_level_context: formData.qa_context_preview?.preview || null,
        knowledge_item_ids: null
      };

      // Call API
      const response = await apiClient.post(
        API_ENDPOINTS.QA_CRITERIA_CREATE,
        createData
      );

      if (response.data.success) {
        onSuccess(response.data.criteria);
      } else {
        setError(response.data.detail || 'Failed to create criteria');
      }
    } catch (error) {
      console.error('Error creating criteria:', error);
      setError(
        error.response?.data?.detail || 'Failed to create criteria. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle close
   */
  const handleCloseWizard = () => {
    setActiveStep(0);
    setFormData({
      name: '',
      team_id: '',
      criteria_type: 'matrix',
      description: '',
      is_active: true,
      is_default: false,
      categories: [],
      qa_context: {
        conversation_type: '',
        participants: '',
        conversation_objective: '',
        ideal_approach: '',
        context_notes: '',
        target_audience: ''
      },
      qa_context_preview: null
    });
    setErrors({});
    setError(null);
    onClose();
  };

  /**
   * Render step content
   */
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <TemplateStep
            criteriaType={formData.criteria_type}
            onCriteriaTypeChange={(v) => handleChange('criteria_type', v)}
            templateMode={templateMode}
            selectedTemplate={selectedTemplate}
            criteriaList={criteriaList || []}
            onTemplateModeChange={handleTemplateModeChange}
            onTemplateSelect={setSelectedTemplate}
          />
        );
      case 1:
        return (
          <BasicInfoStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      case 2:
        return formData.criteria_type === 'boolean' ? (
          <QualificationItemsStep
            formData={formData}
            errors={errors}
            onQualificationItemsChange={handleQualificationItemsChange}
          />
        ) : (
          <CategoriesStep
            formData={formData}
            errors={errors}
            onCategoryChange={handleCategoryChange}
            onAddCategory={handleAddCategory}
            onDeleteCategory={handleDeleteCategory}
          />
        );
      case 3:
        return (
          <QAContextSettingsStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      case 4:
        return (
          <QAContextPreviewStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      case 5:
        return (
          <ReviewStep
            formData={formData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCloseWizard} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            Create New QA Criteria
          </Box>
          <IconButton onClick={handleCloseWizard} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 2 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent dividers>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent(activeStep)}

        {/* Saving Progress */}
        {saving && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleCloseWizard} disabled={saving}>
          Cancel
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || saving}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForward />}
              disabled={saving}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreate}
              startIcon={<Save />}
              disabled={saving}
            >
              {saving ? 'Creating...' : 'Create Criteria'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default QACriteriaWizard;
